import { useState } from 'react';

/**
 * ProductPickCard — Brew's OWN disambiguation card (not a product card).
 *
 * When a prompt could apply to more than one connected product, the engine
 * emits a `brew_select_product` tool-result sidecar:
 *
 *   { prompt: string, options: Array<{ label, value }>, originalPrompt: string }
 *
 * where each option's `value` is a connectorId. Clicking re-sends the ORIGINAL
 * question pinned to that connector (via onPickProduct → send(prompt, {connectorHint})),
 * so the next turn routes deterministically to the chosen product and renders
 * its card. Choosing BETWEEN products is Brew's concern (it owns the connector
 * registry), so this card lives in Brew — it is not a duplicated product card.
 *
 * Rendered natively (not through the federated product registry) and styled
 * with the same .chat-messages CSS tokens the product cards use.
 */

interface ProductOption {
  label: string;
  value: string;
}

interface ProductPickBody {
  prompt?: unknown;
  options?: unknown;
  originalPrompt?: unknown;
}

function narrowOptions(v: unknown): ProductOption[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (o): o is ProductOption =>
      !!o &&
      typeof o === 'object' &&
      typeof (o as { label?: unknown }).label === 'string' &&
      typeof (o as { value?: unknown }).value === 'string',
  );
}

export const PRODUCT_PICK_ACTION = 'brew_select_product';

export function ProductPickCard({
  body,
  onPickProduct,
}: {
  body: unknown;
  /** Re-send the original question pinned to the chosen connector. Absent on a
   *  reloaded historical turn → options render as inert chips. */
  onPickProduct?: (originalPrompt: string, connectorId: string) => void;
}) {
  const b = (body && typeof body === 'object' ? body : {}) as ProductPickBody;
  const prompt = typeof b.prompt === 'string' ? b.prompt : 'Which product did you mean?';
  const originalPrompt = typeof b.originalPrompt === 'string' ? b.originalPrompt : '';
  const options = narrowOptions(b.options);
  const [chosen, setChosen] = useState<string | null>(null);
  const interactive = typeof onPickProduct === 'function' && originalPrompt.length > 0;

  if (options.length === 0) return null;

  return (
    <div
      style={{
        margin: '8px 0',
        padding: '14px',
        border: '1px solid var(--color-border-default, #2a3a5c)',
        borderRadius: 12,
        background: 'var(--color-bg-card, rgba(15, 23, 42, 0.55))',
        color: 'var(--color-text-primary, #e7ecf6)',
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{prompt}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((o, i) => {
          const isChosen = chosen === o.value;
          const disabled = !interactive || chosen !== null;
          return (
            <button
              key={`${o.value}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (!interactive || chosen !== null) return;
                setChosen(o.value);
                onPickProduct!(originalPrompt, o.value);
              }}
              style={{
                textAlign: 'left',
                padding: '9px 12px',
                borderRadius: 9,
                border: `1px solid ${isChosen ? 'var(--color-accent, #2563eb)' : 'var(--color-border-default, #2a3a5c)'}`,
                background: isChosen
                  ? 'var(--color-accent-soft, rgba(37, 99, 235, 0.14))'
                  : 'var(--color-bg-elev, rgba(148, 163, 184, 0.06))',
                color: 'var(--color-text-primary, #e7ecf6)',
                fontSize: 13,
                fontWeight: 500,
                cursor: disabled ? 'default' : 'pointer',
                opacity: !interactive ? 0.6 : chosen !== null && !isChosen ? 0.45 : 1,
                transition: 'background 0.12s, border-color 0.12s, opacity 0.12s',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
